import React from 'react';
import type { LabeledVizSpec } from 'shared/src/api/ai-labeled-viz-types';
import type { NoDataViz } from 'shared/src/api/ai-viz-types';
import type { EntityLinks } from 'shared/src/trpc-types/cube-load-trpc-types';
import { transformLineBarGraph } from 'shared/src/utils/ai/ai-viz-transformers';
import { BarLineChart } from '@/components/ai-visualisations/bar-line-chart';
import { LineChart } from '@/components/ai-visualisations/line-chart';
import { PieDonutChart } from '@/components/ai-visualisations/pie-donut-chart';
import { KpiCard } from '@/components/ai-visualisations/kpi-card';
import { BarChart } from '@/components/ai-visualisations/bar-chart';
import { DataTable } from '@/components/ai-visualisations/data-table';
import { UnsupportedVizPlaceholder } from './unsupported-viz-placeholder';

type ChartRendererProps = {
  viz: LabeledVizSpec | NoDataViz;
  rows: Record<string, unknown>[];
  links?: EntityLinks;
};

export function ChartRenderer({ viz, rows, links }: ChartRendererProps) {
  if (viz.vizType === 'no_data') {
    return (
      <div className="flex items-center justify-center rounded-xl border border-dashed border-border bg-muted/20 p-8 text-center">
        <p className="text-sm text-muted-foreground">{viz.reason}</p>
      </div>
    );
  }

  if (viz.vizType === 'line_bar_graph') {
    const data = transformLineBarGraph(rows, viz);
    return (
      <BarLineChart
        title={viz.title}
        axisFormat={viz.labels.axisFormat}
        data={data}
        planned={{ key: viz.mapping.plannedKey, name: viz.labels.plannedLabel }}
        bars={viz.mapping.barKeys.map(k => ({ key: k, name: viz.labels.barLabels[k] ?? k }))}
      />
    );
  }

  if (viz.vizType === 'bar_chart') {
    return <BarChart rows={rows} viz={viz} />;
  }

  if (viz.vizType === 'pie_donut') {
    return <PieDonutChart rows={rows} viz={viz} />;
  }

  if (viz.vizType === 'line_chart') {
    return <LineChart rows={rows} viz={viz} />;
  }

  if (viz.vizType === 'kpi_card') {
    return <KpiCard rows={rows} viz={viz} />;
  }

  if (viz.vizType === 'table') {
    return <DataTable rows={rows} viz={viz} links={links} />;
  }

  const unhandled = viz as { vizType: string; title: string };
  return <UnsupportedVizPlaceholder vizType={unhandled.vizType} title={unhandled.title} />;
}
